import { useCallback, useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { Link } from 'react-router-dom';
import type {
  FulfillmentAdminClient,
  FulfillmentTracking,
  FulfillmentTrackingTrace,
} from '@/infrastructure/FulfillmentAdminClient';
import { buildFedExTrackingUrl, formatUnifiedShippingStatus } from '@/pages/oms/orders/[id]/FedExShipmentCard';
import { TrackingMoreTimeline } from '@/pages/oms/orders/[id]/TrackingMoreTimeline';
import { getStatusDescription, getStatusText } from '@/pages/oms/orders/utils/statusTextMapping';
import { translateStatus } from '@/pages/oms/orders/utils/translate';
import { ROUTES } from '@/routes/routes';
import {
  carrierKindOf,
  deleoTrackingOf,
  feedOf,
  latestEventOf,
  outboundTrackingOf,
  storedParxlEventsOf,
  trackingMoreOf,
  unifiedEventsOf,
  yunTrackingOf,
  type CarrierKind,
} from '@/utils/fulfillmentCarrierTracking';
import { kstTimestamp } from '@/utils/fulfillmentTime';

/**
 * Whether this order may ask the carrier live through the V2 trace route. Before launch OMS Center owns a web order's
 * carrier order, so its live trace stays there; a 2.0-born order exists only in V2 and is always traced here.
 */
export type LiveTraceAccess = 'allowed' | 'checking' | 'awaits-launch';

type TraceClient = Pick<FulfillmentAdminClient, 'getTrackingTrace'>;

type LiveTraceState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'loaded'; trace: FulfillmentTrackingTrace }
  | { phase: 'failed'; untraceable: boolean };

const DASH = '—';

const CARRIER_BADGES: Record<CarrierKind, { label: string; background: string }> = {
  YUN_EXPRESS: { label: 'YUN', background: '#198754' },
  FEDEX: { label: 'FEDEX', background: '#4d148c' },
  DELEO: { label: 'DELEO', background: '#0d6efd' },
  PARXL: { label: 'PARXL', background: '#0dcaf0' },
  OTHER: { label: 'CARRIER', background: '#6c757d' },
};

/** A carrier's own stamp with no zone is its local time; shown as it came rather than guessed into KST. */
const asGiven = (value: string | null | undefined): string => value || DASH;

const EmptyRow = ({ colSpan, children }: { colSpan: number; children: string }) => (
  <tr>
    <td colSpan={colSpan} className="blocked-note">
      {children}
    </td>
  </tr>
);

const useLiveTrace = (client: TraceClient, orderId: number, trackingNumber: string) => {
  const [state, setState] = useState<LiveTraceState>({ phase: 'idle' });
  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      setState({ phase: 'loaded', trace: await client.getTrackingTrace(orderId, trackingNumber) });
    } catch (error) {
      // 404: no carrier order on the row (or a carrier with no provider), so asking again will not help.
      setState({ phase: 'failed', untraceable: isAxiosError(error) && error.response?.status === 404 });
    }
  }, [client, orderId, trackingNumber]);
  return { state, load };
};

/** The live carrier answer, one table for every leg; Deleo's Korean descriptions read as OMS Center shows them. */
const LiveTraceTable = ({ trace, kind }: { trace: FulfillmentTrackingTrace; kind: CarrierKind }) => {
  const rows = (trace.trackingInfoList ?? []).flatMap((info) =>
    (info.events ?? [])
      .filter((event) => event.eventTime)
      .map((event) => ({ leg: info.trackingNumber, carrier: info.carrierName, ...event })),
  );
  return (
    <table className="tbl" aria-label="Live trace">
      <thead>
        <tr>
          <th>Time (carrier)</th>
          <th>Status</th>
          <th>Description</th>
          <th>Location</th>
          <th>Carrier / No</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <EmptyRow colSpan={5}>The carrier has no tracking history for this parcel yet.</EmptyRow>
        ) : (
          rows.map((row, index) => (
            <tr key={`${row.leg}-${row.eventTime}-${index}`}>
              <td className="num">{asGiven(row.eventTime)}</td>
              <td>{row.status ? <span className="badge bg-secondary">{row.status}</span> : DASH}</td>
              <td>{kind === 'DELEO' ? translateStatus(row.description) : row.description || DASH}</td>
              <td>{row.location || DASH}</td>
              <td className="num">{[row.carrier, row.leg].filter(Boolean).join(' · ') || DASH}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

interface LiveTraceSectionProps {
  orderId: number;
  kind: CarrierKind;
  access: LiveTraceAccess;
  state: LiveTraceState;
  onLoad: () => void;
}

const LiveTraceSection = ({ orderId, kind, access, state, onLoad }: LiveTraceSectionProps) => (
  <>
    <div className="minih">LIVE TRACE</div>
    {access === 'awaits-launch' ? (
      <div className="blocked-note" data-testid="live-trace-awaits-launch">
        Web order before launch: OMS Center reads this parcel&apos;s live trace until Fulfillment 2.0 launches.{' '}
        <Link to={ROUTES.oms.orderDetail(String(orderId))}>Open #{orderId} in OMS Center</Link>
      </div>
    ) : access === 'checking' ? (
      <div className="blocked-note">Checking whether Fulfillment 2.0 has launched</div>
    ) : (
      <>
        <button type="button" className="btn btn-line btn-sm" disabled={state.phase === 'loading'} onClick={onLoad}>
          {state.phase === 'loading'
            ? 'Asking the carrier…'
            : state.phase === 'idle'
              ? 'Load live trace'
              : '↻ Reload live trace'}
        </button>
        {state.phase === 'failed' && (
          <div className="blocked-note" role="alert">
            {state.untraceable
              ? 'This parcel has no carrier order to trace yet.'
              : 'The carrier did not answer the live trace. Try again shortly; the stored history above is its latest report.'}
          </div>
        )}
        {state.phase === 'loaded' && <LiveTraceTable trace={state.trace} kind={kind} />}
      </>
    )}
  </>
);

const StatusHistory = ({ row }: { row: FulfillmentTracking }) => {
  const history = [...(row.snapshot?.statusHistory ?? [])].reverse();
  if (history.length === 0) {
    return null;
  }
  return (
    <>
      <div className="minih">STATUS HISTORY</div>
      <table className="tbl" aria-label="Status history">
        <thead>
          <tr>
            <th>Time</th>
            <th>Status</th>
            <th>Feed</th>
            <th>Carrier code</th>
          </tr>
        </thead>
        <tbody>
          {history.map((entry, index) => (
            <tr key={`${entry.timestamp}-${entry.status}-${index}`}>
              <td className="num">{kstTimestamp(entry.timestamp)}</td>
              <td>{formatUnifiedShippingStatus(entry.status)}</td>
              <td>{entry.provider}</td>
              <td>{entry.providerStatusCode || DASH}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/** The unified events, as OMS Center's FedEx card shows them when TrackingMore has no checkpoints. */
const UnifiedEvents = ({ row }: { row: FulfillmentTracking }) => {
  const events = unifiedEventsOf(row);
  return (
    <>
      <div className="minih">TRACKING HISTORY</div>
      <table className="tbl" aria-label="Tracking events">
        <thead>
          <tr>
            <th>Time</th>
            <th>Event</th>
            <th>Description</th>
            <th>Carrier code</th>
          </tr>
        </thead>
        <tbody>
          {events.length === 0 ? (
            <EmptyRow colSpan={4}>No tracking events yet</EmptyRow>
          ) : (
            events.map((event, index) => (
              <tr key={`${event.timestamp}-${event.providerEventCode}-${index}`}>
                <td className="num">{kstTimestamp(event.timestamp)}</td>
                <td>{formatUnifiedShippingStatus(event.event)}</td>
                <td>{event.displayText || event.description || DASH}</td>
                <td>{event.providerEventCode || DASH}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
};

const YunHistory = ({ row }: { row: FulfillmentTracking }) => {
  const yun = yunTrackingOf(row);
  const events = yun?.events ?? [];
  return (
    <>
      {yun?.lastMileSite && (
        <div className="ftbadge">
          <a href={yun.lastMileSite} target="_blank" rel="noreferrer" className="btn btn-line btn-sm">
            Last mile: {yun.lastMileName || 'Carrier tracking'}
          </a>
          {yun.lastMileNumber && yun.lastMileNumber !== row.number && (
            <span className="k">Last-mile no. {yun.lastMileNumber}</span>
          )}
        </div>
      )}
      <div className="minih">
        TRACKING HISTORY{yun && <span className="k"> (synced {kstTimestamp(yun.syncedAt)})</span>}
      </div>
      <table className="tbl" aria-label="YunExpress tracking history">
        <thead>
          <tr>
            <th>Time (local)</th>
            <th>Node</th>
            <th>Description</th>
            <th>Location</th>
            <th>KST</th>
          </tr>
        </thead>
        <tbody>
          {events.length === 0 ? (
            <EmptyRow colSpan={5}>No tracking events yet — load the live trace below.</EmptyRow>
          ) : (
            events.map((event, index) => (
              <tr key={`${event.utcTime ?? event.localTime}-${event.node}-${index}`}>
                <td className="num">{asGiven(event.localTime)}</td>
                <td>{event.node ? <span className="badge bg-secondary">{event.node}</span> : DASH}</td>
                <td>{event.description || DASH}</td>
                <td>{event.location || DASH}</td>
                <td className="num">{event.utcTime ? kstTimestamp(event.utcTime) : DASH}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
};

const FedExHistory = ({ row }: { row: FulfillmentTracking }) => {
  const trackingMore = trackingMoreOf(row);
  return trackingMore ? <TrackingMoreTimeline tracking={trackingMore} /> : <UnifiedEvents row={row} />;
};

const DeleoHistory = ({ row }: { row: FulfillmentTracking }) => {
  const deleo = deleoTrackingOf(row);
  const statusName = deleo?.status.name ?? '';
  return (
    <>
      <div className="minih">
        DELEO ORDER{deleo && <span className="k"> (synced {kstTimestamp(deleo.syncedAt)})</span>}
      </div>
      <table className="tbl" aria-label="Deleo order">
        <thead>
          <tr>
            <th>Provider Order ID</th>
            <th>Tracking No</th>
            <th>Status Code</th>
            <th>Status Description</th>
            <th>Status KR</th>
          </tr>
        </thead>
        <tbody>
          {deleo ? (
            <tr>
              <td className="num">{deleo.deleoOrderId || DASH}</td>
              <td className="num">{deleo.deleoTrackingNo || DASH}</td>
              <td>{[deleo.status.code, deleo.status.name].filter(Boolean).join(' ') || DASH}</td>
              <td>{getStatusText(statusName) || DASH}</td>
              <td>{getStatusDescription(statusName) || DASH}</td>
            </tr>
          ) : (
            <EmptyRow colSpan={5}>Deleo has not reported on this parcel yet</EmptyRow>
          )}
        </tbody>
      </table>
      <div className="minih">CARRIER TRACKING</div>
      <table className="tbl" aria-label="Deleo carrier tracking">
        <thead>
          <tr>
            <th>Carrier</th>
            <th>Carrier Tracking No</th>
            <th>SS Request ID</th>
            <th>Created On</th>
            <th>Updated On</th>
            <th>WMS ASN Code</th>
            <th>Provider Tracking No</th>
          </tr>
        </thead>
        <tbody>
          {!deleo || deleo.handOvers.length === 0 ? (
            <EmptyRow colSpan={7}>Package hasn&apos;t been handed over to a carrier yet</EmptyRow>
          ) : (
            deleo.handOvers.map((handOver, index) => (
              <tr key={`${handOver.trackingNo}-${index}`}>
                <td>{handOver.carrierName || DASH}</td>
                <td className="num">{handOver.trackingNo || DASH}</td>
                <td className="num">{handOver.requestId || DASH}</td>
                <td className="num">{asGiven(handOver.createdOn)}</td>
                <td className="num">{asGiven(handOver.updatedOn)}</td>
                <td className="num">{handOver.wmsAsnCode || DASH}</td>
                <td className="num">{handOver.deleoTrackingNo || DASH}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="minih">DELIVERY TRACE</div>
      <table className="tbl" aria-label="Deleo delivery trace">
        <thead>
          <tr>
            <th>Time (carrier)</th>
            <th>Status</th>
            <th>Description</th>
            <th>Location</th>
          </tr>
        </thead>
        <tbody>
          {!deleo || deleo.trace.length === 0 ? (
            <EmptyRow colSpan={4}>No delivery trace stored yet — load the live trace below.</EmptyRow>
          ) : (
            deleo.trace.map((event, index) => (
              <tr key={`${event.eventTime}-${event.status}-${index}`}>
                <td className="num">{asGiven(event.eventTime)}</td>
                <td>{event.status ? <span className="badge bg-secondary">{event.status}</span> : DASH}</td>
                <td>{translateStatus(event.description)}</td>
                <td>{event.location || DASH}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
};

/** Parxl history is the live answer (no Parxl webhook exists); the stored copy stands in when Parxl does not answer. */
const ParxlStoredHistory = ({ row }: { row: FulfillmentTracking }) => {
  const events = storedParxlEventsOf(row);
  return (
    <>
      <div className="minih">TRACKING HISTORY (stored)</div>
      <table className="tbl" aria-label="Stored Parxl tracking history">
        <thead>
          <tr>
            <th>Date</th>
            <th>Status</th>
            <th>Description</th>
            <th>Location</th>
          </tr>
        </thead>
        <tbody>
          {events.length === 0 ? (
            <EmptyRow colSpan={4}>No tracking events yet</EmptyRow>
          ) : (
            events.map((event, index) => (
              <tr key={`${event.date}-${event.status}-${index}`}>
                <td className="num">{asGiven(event.date)}</td>
                <td>{event.status ? <span className="badge bg-secondary">{event.status}</span> : DASH}</td>
                <td>{event.description || DASH}</td>
                <td>{event.location || DASH}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
};

/** The carrier's own status as its latest report put it. */
const carrierStatusOf = (row: FulfillmentTracking, kind: CarrierKind): string | null => {
  switch (kind) {
    case 'YUN_EXPRESS':
      return feedOf(row, 'YUN_EXPRESS')?.providerStatus ?? null;
    case 'DELEO':
      return feedOf(row, 'DELEO')?.providerStatus ?? null;
    case 'PARXL':
      return feedOf(row, 'PARXL')?.providerStatus ?? null;
    default:
      return feedOf(row, 'TRACKING_MORE')?.providerStatus ?? null;
  }
};

interface CarrierTrackingCardProps {
  orderId: number;
  row: FulfillmentTracking;
  client: TraceClient;
  liveTrace: LiveTraceAccess;
}

const CarrierTrackingCard = ({ orderId, row, client, liveTrace }: CarrierTrackingCardProps) => {
  const kind = carrierKindOf(row.carrier);
  const badge = CARRIER_BADGES[kind];
  const latest = latestEventOf(row);
  const { state, load } = useLiveTrace(client, orderId, row.number);
  // Parxl has no stored feed of its own until the refresh lands, so its history is asked for as the card opens.
  const autoLoad = kind === 'PARXL' && liveTrace === 'allowed';
  useEffect(() => {
    if (autoLoad) {
      void load();
    }
  }, [autoLoad, load]);

  return (
    <div className="carrier-card" data-testid={`carrier-tracking-card-${row.number}`}>
      <div className="ftbadge">
        <span className="badge text-white" style={{ backgroundColor: badge.background }}>
          {kind === 'OTHER' ? row.carrier || badge.label : badge.label}
        </span>
        <span className="num">{row.number}</span>
        {kind === 'FEDEX' && (
          <a href={buildFedExTrackingUrl(row.number)} target="_blank" rel="noreferrer" className="btn btn-line btn-sm">
            Track on FedEx
          </a>
        )}
      </div>
      <table className="tbl" aria-label="Carrier shipment">
        <thead>
          <tr>
            <th>Provider Order ID</th>
            <th>Tracking Number</th>
            <th>Unified Status</th>
            <th>Carrier Status</th>
            <th>Latest Event</th>
            <th>Last Report</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="num">{row.carrierOrderId || DASH}</td>
            <td className="num">{row.number}</td>
            <td>{formatUnifiedShippingStatus(row.unifiedStatus)}</td>
            <td>{carrierStatusOf(row, kind) || DASH}</td>
            <td>
              {latest
                ? `${latest.displayText || latest.description || latest.event} · ${kstTimestamp(latest.timestamp)}`
                : DASH}
            </td>
            <td className="num">{row.snapshot ? kstTimestamp(row.snapshot.lastReceivedAt) : DASH}</td>
          </tr>
        </tbody>
      </table>

      {kind === 'YUN_EXPRESS' && <YunHistory row={row} />}
      {kind === 'FEDEX' && <FedExHistory row={row} />}
      {kind === 'DELEO' && <DeleoHistory row={row} />}
      {kind === 'PARXL' && (state.phase === 'failed' || liveTrace !== 'allowed') && <ParxlStoredHistory row={row} />}
      {kind === 'OTHER' && <UnifiedEvents row={row} />}

      <StatusHistory row={row} />
      <LiveTraceSection orderId={orderId} kind={kind} access={liveTrace} state={state} onLoad={() => void load()} />
    </div>
  );
};

interface CarrierTrackingCardsProps {
  orderId: number;
  tracking: FulfillmentTracking[];
  client: TraceClient;
  liveTrace: LiveTraceAccess;
}

/** One card per outbound parcel with what OMS Center shows of it, read from the V2 tracking row (SSD-7063). */
export function CarrierTrackingCards({ orderId, tracking, client, liveTrace }: CarrierTrackingCardsProps) {
  const parcels = outboundTrackingOf(orderId, tracking);
  if (parcels.length === 0) {
    return null;
  }
  return (
    <div data-testid="carrier-tracking-cards">
      {parcels.map((row) => (
        <CarrierTrackingCard key={row.number} orderId={orderId} row={row} client={client} liveTrace={liveTrace} />
      ))}
    </div>
  );
}
